import { useEffect } from 'react';
import { FacesheetSelector } from '@/feature/profiles/components/FacesheetSelector';
import {
  Section,
  SectionHeader,
  SectionContent,
  useFormContext,
  FormDescription,
  FormMessage,
  FormField,
} from '@predoc/cow';
import { NestedRequestActionFormFieldProps } from './validation';
import { usePatientRequestContext } from '@/feature/patients/context/usePatientRequestContext';
import { useCanCreateStandaloneFacesheetRequest } from '@/feature/patients/hooks/patientsAPI';
import { Facesheet } from '@/feature/profiles/types';
import { getAllowedFacesheets, isFacesheetAllowed } from '../facesheetsRules';

export const Facesheets = () => {
  const { control, watch, setValue } = useFormContext();
  const { requestState, isEditMode, patientId } = usePatientRequestContext();
  const selectedRequestMethods = requestState.request?.selectedRequestMethods;
  const selectedFacesheets = watch('FACESHEETS.selectedFacesheets') as
    | Facesheet[]
    | undefined;
  const standaloneFacesheetPatientId = !isEditMode
    ? patientId || requestState.patient?.id
    : undefined;
  const { data: standaloneFacesheetAccess } =
    useCanCreateStandaloneFacesheetRequest(standaloneFacesheetPatientId);
  const allowStandaloneFacesheets =
    standaloneFacesheetAccess?.canCreate === true;

  useEffect(() => {
    if (isEditMode) return;
    const currentSelectedRequestMethods = selectedRequestMethods ?? [];
    const currentSelectedFacesheets = selectedFacesheets ?? [];

    const allowedFacesheets = getAllowedFacesheets({
      selectedMethods: currentSelectedRequestMethods,
      facesheets: currentSelectedFacesheets,
      allowStandaloneFacesheets,
    });

    const hasChanged =
      allowedFacesheets.length !== currentSelectedFacesheets.length ||
      allowedFacesheets.some(
        (facesheet: Facesheet, index: number) =>
          facesheet.facesheetConfigId !==
          currentSelectedFacesheets[index]?.facesheetConfigId,
      );

    if (hasChanged) {
      setValue('FACESHEETS.selectedFacesheets', allowedFacesheets, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    allowStandaloneFacesheets,
    isEditMode,
    selectedFacesheets,
    selectedRequestMethods,
    setValue,
  ]);

  return (
    <Section className="bg-white">
      <SectionHeader>
        <h1 className="text-xl font-bold">Facesheets</h1>
      </SectionHeader>
      <SectionContent>
        <FormField
          control={control}
          name="FACESHEETS.selectedFacesheets"
          render={({
            field,
          }: NestedRequestActionFormFieldProps<'FACESHEETS.selectedFacesheets'>) => (
            <div className="space-y-2">
              <FacesheetSelector
                profileId={requestState.organizationProfileId}
                values={field.value || []}
                onChange={field.onChange}
                placeholder="Select Facesheets"
                filterFacesheet={
                  isEditMode
                    ? undefined
                    : (facesheet: Facesheet) =>
                        isFacesheetAllowed({
                          selectedMethods: selectedRequestMethods,
                          facesheet,
                          allowStandaloneFacesheets,
                        })
                }
              />
              <FormMessage />
              <FormDescription>
                Select all facesheets you want to generate.
              </FormDescription>
            </div>
          )}
        />
      </SectionContent>
    </Section>
  );
};
